/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { message } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Sidebar from './components/Sidebar';
import ParamPanel from './components/ParamPanel';
import CodePanel from './components/CodePanel';
import ContentPanel from './components/ContentPanel';
import PricingTablePanel from './components/PricingTablePanel';
import CostQueryPanel from './components/CostQueryPanel';
import {
  buildParamsFromRequestBody,
  buildInitialParams,
  normalizeModelParams,
} from './utils/requestNormalization';
import './styles/playground.css';

const Playground = () => {
  const [config, setConfig] = useState(null);
  const [pricingConfig, setPricingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageContent, setPageContent] = useState(null);
  const [viewMode, setViewMode] = useState('test'); // 'test' | 'page' | 'pricing'
  const [params, setParams] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [currentEndpoint, setCurrentEndpoint] = useState(null);
  const [activeTabId, setActiveTabId] = useState(null);
  const [notice, setNotice] = useState({
    enabled: false,
    title: '',
    content: '',
    tutorial_title: '',
    tutorial_content: '',
  });
  const [noticeOpen, setNoticeOpen] = useState(false);
  const [activeNoticeTab, setActiveNoticeTab] = useState('announcement');

  // Sidebar resize
  const [sidebarWidth, setSidebarWidth] = useState(240);
  const sidebarResizeDrag = useRef({
    active: false,
    startX: 0,
    startWidth: 240,
  });

  // Code panel resize
  const [codePanelWidth, setCodePanelWidth] = useState(480);
  const resizeDrag = useRef({ active: false, startX: 0, startWidth: 480 });

  useEffect(() => {
    const onMouseMove = (e) => {
      if (sidebarResizeDrag.current.active) {
        const delta = e.clientX - sidebarResizeDrag.current.startX;
        const next = Math.max(
          180,
          Math.min(480, sidebarResizeDrag.current.startWidth + delta)
        );
        setSidebarWidth(next);
        return;
      }
      if (!resizeDrag.current.active) return;
      const delta = resizeDrag.current.startX - e.clientX;
      const next = Math.max(
        280,
        Math.min(900, resizeDrag.current.startWidth + delta)
      );
      setCodePanelWidth(next);
    };
    const onMouseUp = () => {
      if (sidebarResizeDrag.current.active) {
        sidebarResizeDrag.current.active = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        return;
      }
      if (!resizeDrag.current.active) return;
      resizeDrag.current.active = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const handleSidebarResizeMouseDown = (e) => {
    sidebarResizeDrag.current = {
      active: true,
      startX: e.clientX,
      startWidth: sidebarWidth,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeMouseDown = (e) => {
    resizeDrag.current = {
      active: true,
      startX: e.clientX,
      startWidth: codePanelWidth,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // Playground boot data is fetched once when the route mounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadConfig();
    loadPricingConfig();
    loadNotice();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await axios.get('/api/playground/config');
      if (res.data.success) {
        setConfig(res.data.data);
        if (res.data.data.categories && res.data.data.categories.length > 0) {
          const firstCategory = res.data.data.categories[0];
          setSelectedCategory(firstCategory);

          // 支持新的三级结构（vendors）和旧的二级结构（models）
          let firstModel = null;
          if (firstCategory.vendors && firstCategory.vendors.length > 0) {
            // 新的三级结构：类型 -> 厂商 -> 模型
            const firstVendor = firstCategory.vendors[0];
            if (firstVendor.models && firstVendor.models.length > 0) {
              firstModel = firstVendor.models[0];
            }
          } else if (firstCategory.models && firstCategory.models.length > 0) {
            // 旧的二级结构：类型 -> 模型
            firstModel = firstCategory.models[0];
          }

          if (firstModel) {
            setSelectedModel(firstModel);

            // 如果是"高级用法"分类，自动加载第一个文档页面
            if (
              firstCategory.id === 'advanced' &&
              firstModel.pages &&
              firstModel.pages.length > 0
            ) {
              const firstPage = firstModel.pages[0];
              setSelectedPage(firstPage);
              setViewMode('page');
              // 加载页面内容
              try {
                const response = await fetch(firstPage.content_file);
                if (response.ok) {
                  const content = await response.text();
                  setPageContent(content);
                }
              } catch (error) {
                console.error('Failed to load page content:', error);
              }
            } else {
              // 如果模型没有pages，初始化参数
              if (!firstModel.pages || firstModel.pages.length === 0) {
                initializeParams(firstModel);
              }
            }
          }
        }
      } else {
        message.error('加载配置失败: ' + res.data.message);
      }
    } catch (error) {
      message.error('加载配置失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPricingConfig = async () => {
    try {
      const res = await axios.get('/api/pricing/config');
      if (res.data.success) {
        setPricingConfig(res.data.data);
      } else {
        console.error('加载计费配置失败:', res.data.message);
      }
    } catch (error) {
      console.error('加载计费配置失败:', error.message);
    }
  };

  const loadNotice = async () => {
    try {
      const res = await axios.get('/api/playground/notice');
      if (res.data.success) {
        const nextNotice = res.data.data || {};
        setNotice(nextNotice);
        if (
          nextNotice.enabled &&
          (nextNotice.content || nextNotice.tutorial_content)
        ) {
          setActiveNoticeTab(nextNotice.content ? 'announcement' : 'tutorial');
          setNoticeOpen(true);
        }
      } else {
        console.error('加载 Playground 公告失败:', res.data.message);
      }
    } catch (error) {
      console.error('加载 Playground 公告失败:', error.message);
    }
  };

  const initializeParams = (model) => {
    setParams(buildInitialParams(model));
  };

  const handleSelectModel = (category, model) => {
    setSelectedCategory(category);
    setSelectedModel(model);
    setSelectedPage(null);
    setPageContent(null);
    setTestResult(null);

    // 如果是"高级用法"分类，直接显示第一个文档页面
    if (category.id === 'advanced' && model.pages && model.pages.length > 0) {
      const firstPage = model.pages[0];
      handleSelectPage(firstPage, model, category);
      return;
    }

    // 如果模型没有pages，直接初始化参数并显示测试面板
    if (!model.pages || model.pages.length === 0) {
      initializeParams(model);
      setViewMode('test');
    } else {
      // 如果有pages，保持测试模式但不自动初始化参数
      setViewMode('test');
      initializeParams(model);
    }
  };

  const handleSelectPage = async (page, model, category) => {
    setSelectedPage(page);
    setSelectedModel(model);
    setSelectedCategory(category);
    setViewMode('page');
    setPageContent(null);

    // 加载页面内容
    try {
      const response = await fetch(page.content_file);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const content = await response.text();
      setPageContent(content);
    } catch (error) {
      message.error('加载页面内容失败: ' + error.message);
      setPageContent(
        '# 加载失败\n\n无法加载页面内容，请检查文件路径是否正确。'
      );
    }
  };

  const handleParamChange = (paramName, value) => {
    setParams((prev) => ({
      ...prev,
      [paramName]: value,
    }));
  };

  const normalizeRequestBody = (body, model) => {
    if (!model) {
      return { ...body };
    }

    const normalized = normalizeModelParams(body, model);

    // 主体创建 tab：将扁平表单字段组装为 model_parameters 结构
    if (activeTabId === 'subject-create') {
      const mp = {};
      if (normalized.element_name) mp.element_name = normalized.element_name;
      if (normalized.element_description)
        mp.element_description = normalized.element_description;
      const refType = normalized.reference_type || 'image_refer';
      mp.reference_type = refType;
      normalized.reference_type = refType;
      if (
        normalized.reference_type === 'image_refer' &&
        normalized.frontal_image
      ) {
        mp.element_image_list = { frontal_image: normalized.frontal_image };
        if (normalized.refer_images) {
          try {
            const parsed =
              typeof normalized.refer_images === 'string'
                ? JSON.parse(normalized.refer_images)
                : normalized.refer_images;
            if (Array.isArray(parsed) && parsed.length > 0) {
              mp.element_image_list.refer_images = parsed;
            }
          } catch (e) {
            /* ignore */
          }
        }
      }
      if (normalized.reference_type === 'video_refer' && normalized.video_url) {
        mp.element_video_list = { video_url: normalized.video_url };
      }
      if (normalized.element_voice_id)
        mp.element_voice_id = normalized.element_voice_id;
      if (normalized.tag_list) {
        try {
          const parsed =
            typeof normalized.tag_list === 'string'
              ? JSON.parse(normalized.tag_list)
              : normalized.tag_list;
          if (Array.isArray(parsed) && parsed.length > 0) {
            mp.tag_list = parsed;
          }
        } catch (e) {
          /* ignore */
        }
      }
      const result = { model: normalized.model, model_parameters: mp };
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        result.byPass = normalized.byPass;
      }
      return result;
    }

    // 主体列表 tab：只保留相关字段
    if (activeTabId === 'subject-list') {
      const body = { model: normalized.model };
      body.type = normalized.type || 'Custom-Elements';
      if (normalized.pageNum) body.pageNum = parseInt(normalized.pageNum) || 1;
      if (normalized.pageSize)
        body.pageSize = parseInt(normalized.pageSize) || 30;
      return body;
    }

    // 主体查询 tab：只保留 model、task_id、byPass
    if (activeTabId === 'subject-query') {
      const body = { model: normalized.model };
      if (normalized.task_id !== undefined && normalized.task_id !== '')
        body.task_id = normalized.task_id;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 素材组合创建
    if (activeTabId === 'asset-group-create') {
      const body = { model: normalized.model };
      if (normalized.name) body.name = normalized.name;
      if (normalized.description) body.description = normalized.description;
      if (normalized.group_type) body.group_type = normalized.group_type;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 素材创建
    if (activeTabId === 'asset-create') {
      const body = { model: normalized.model };
      if (normalized.group_id) body.group_id = normalized.group_id;
      if (normalized.url) body.url = normalized.url;
      body.asset_type = normalized.asset_type || 'Image';
      if (normalized.name) body.name = normalized.name;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 查询素材组合
    if (activeTabId === 'asset-group-get') {
      const body = { model: normalized.model };
      if (normalized.id) body.id = normalized.id;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 查询素材
    if (activeTabId === 'asset-get') {
      const body = { model: normalized.model };
      if (normalized.id) body.id = normalized.id;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 素材组合列表
    if (activeTabId === 'asset-group-list') {
      const body = { model: normalized.model };
      if (normalized.filters) {
        try {
          body.filters =
            typeof normalized.filters === 'string'
              ? JSON.parse(normalized.filters)
              : normalized.filters;
        } catch {
          body.filters = normalized.filters;
        }
      }
      body.page_number = normalized.page_number
        ? Number(normalized.page_number)
        : 1;
      body.page_size = normalized.page_size ? Number(normalized.page_size) : 10;
      if (normalized.sort_by) body.sort_by = normalized.sort_by;
      if (normalized.sort_order) body.sort_order = normalized.sort_order;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 更新素材组合
    if (activeTabId === 'asset-group-update') {
      const body = { model: normalized.model };
      if (normalized.id) body.id = normalized.id;
      if (normalized.name) body.name = normalized.name;
      if (normalized.description) body.description = normalized.description;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 素材列表
    if (activeTabId === 'asset-list') {
      const body = { model: normalized.model };
      if (normalized.filters) {
        try {
          body.filters =
            typeof normalized.filters === 'string'
              ? JSON.parse(normalized.filters)
              : normalized.filters;
        } catch {
          body.filters = normalized.filters;
        }
      }
      body.page_number = normalized.page_number
        ? Number(normalized.page_number)
        : 1;
      body.page_size = normalized.page_size ? Number(normalized.page_size) : 10;
      if (normalized.sort_by) body.sort_by = normalized.sort_by;
      if (normalized.sort_order) body.sort_order = normalized.sort_order;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 更新素材
    if (activeTabId === 'asset-update') {
      const body = { model: normalized.model };
      if (normalized.id) body.id = normalized.id;
      if (normalized.name) body.name = normalized.name;
      if (normalized.project_name) body.project_name = normalized.project_name;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    // 字幕擦除提交
    if (activeTabId === 'subtitle-erase') {
      return {
        model: normalized.model || 'Doubao-subtitle-erase',
        video_url: normalized.video_url || '',
      };
    }

    // 字幕擦除查询
    if (activeTabId === 'subtitle-erase-query') {
      return {
        model: normalized.model || 'Doubao-subtitle-erase',
        task_id: normalized.task_id || '',
      };
    }

    // 超分结果查询（图片/视频）：只保留 model、task_id、非空 byPass
    if (
      activeTabId === 'image-upscale-query' ||
      activeTabId === 'video-upscale-query'
    ) {
      const body = { model: normalized.model };
      if (normalized.task_id !== undefined && normalized.task_id !== '')
        body.task_id = normalized.task_id;
      if (
        normalized.byPass &&
        typeof normalized.byPass === 'object' &&
        !Array.isArray(normalized.byPass) &&
        Object.keys(normalized.byPass).length > 0
      ) {
        body.byPass = normalized.byPass;
      }
      return body;
    }

    return normalized;
  };

  const handleTest = async (apiKey, endpoint) => {
    if (!selectedModel) {
      message.error('请先选择模型');
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const requestBody = normalizeRequestBody(params, selectedModel);

      // 主体创建必填参数校验
      if (activeTabId === 'subject-create') {
        const mp = requestBody.model_parameters || {};
        const missing = [];
        if (!mp.element_name) missing.push('element_name（主体名称）');
        if (!mp.element_description)
          missing.push('element_description（主体描述）');
        if (mp.reference_type === 'image_refer') {
          if (!mp.element_image_list || !mp.element_image_list.frontal_image)
            missing.push('frontal_image（正面参考图）');
          if (
            !mp.element_image_list ||
            !mp.element_image_list.refer_images ||
            mp.element_image_list.refer_images.length === 0
          )
            missing.push('refer_images（其他角度参考图）');
        } else if (mp.reference_type === 'video_refer') {
          if (!mp.element_video_list || !mp.element_video_list.video_url)
            missing.push('video_url（参考视频）');
        }
        if (missing.length > 0) {
          message.error('请填写必填参数：' + missing.join('、'));
          setTesting(false);
          return;
        }
      }

      const res = await axios.post('/api/playground/execute', {
        endpoint: endpoint || selectedModel.endpoint,
        method: selectedModel.method,
        body: requestBody,
        api_key: apiKey,
      });

      if (res.data.success) {
        setTestResult(res.data.data);
        message.success('测试成功');
      } else {
        message.error('测试失败: ' + res.data.message);
        setTestResult({ error: res.data.message });
      }
    } catch (error) {
      message.error('测试失败: ' + error.message);
      setTestResult({ error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleShowPricingTable = () => {
    setViewMode('pricing');
  };

  if (loading) {
    return (
      <div className='playground-loading'>
        <div className='loading-spinner'></div>
        <div>加载中...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className='playground-error'>
        <div>配置加载失败</div>
      </div>
    );
  }

  return (
    <>
      {noticeOpen && (
        <div
          className='playground-notice-overlay'
          onClick={() => setNoticeOpen(false)}
        >
          <div
            className='playground-notice-card'
            role='dialog'
            aria-modal='true'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='playground-notice-header'>
              <div className='playground-notice-tabs'>
                <button
                  className={`playground-notice-tab${
                    activeNoticeTab === 'announcement' ? ' active' : ''
                  }`}
                  type='button'
                  onClick={() => setActiveNoticeTab('announcement')}
                >
                  {notice.title || '更新公告'}
                </button>
                <button
                  className={`playground-notice-tab${
                    activeNoticeTab === 'tutorial' ? ' active' : ''
                  }`}
                  type='button'
                  onClick={() => setActiveNoticeTab('tutorial')}
                >
                  {notice.tutorial_title || '使用教程'}
                </button>
              </div>
              <button
                className='playground-notice-close'
                type='button'
                onClick={() => setNoticeOpen(false)}
                aria-label='关闭公告'
              >
                ×
              </button>
            </div>
            <div className='playground-notice-modal'>
              {activeNoticeTab === 'announcement' && (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {notice.content || ''}
                </ReactMarkdown>
              )}
              {activeNoticeTab === 'tutorial' && (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {notice.tutorial_content || ''}
                </ReactMarkdown>
              )}
            </div>
            <div className='playground-notice-footer'>
              <button
                className='playground-notice-confirm'
                type='button'
                onClick={() => setNoticeOpen(false)}
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}

      <div className='playground-container'>
        <Sidebar
          config={config}
          selectedModel={selectedModel}
          selectedCategory={selectedCategory}
          selectedPage={selectedPage}
          onSelectModel={handleSelectModel}
          onSelectPage={handleSelectPage}
          onShowPricingTable={handleShowPricingTable}
          style={{ width: sidebarWidth }}
        />
        <div
          className='playground-resize-handle'
          onMouseDown={handleSidebarResizeMouseDown}
        />

        {viewMode === 'pricing' ? (
          <PricingTablePanel
            config={config}
            pricingConfig={pricingConfig}
            onBack={() => setViewMode('test')}
          />
        ) : selectedCategory?.id === 'cost' ? (
          <CostQueryPanel category={selectedCategory} />
        ) : viewMode === 'test' ? (
          <>
            <ParamPanel
              model={selectedModel}
              params={params}
              onParamChange={handleParamChange}
              testing={testing}
              testResult={testResult}
              onTest={handleTest}
              category={selectedCategory}
              onEndpointChange={setCurrentEndpoint}
              onTabChange={(tabId) => {
                setActiveTabId(tabId);
                // 切换 tab 时重置参数，避免跨 tab 字段残留
                if (selectedModel) {
                  setParams(buildInitialParams(selectedModel));
                }
              }}
            />
            <div
              className='playground-resize-handle'
              onMouseDown={handleResizeMouseDown}
            />
            <CodePanel
              model={selectedModel}
              params={params}
              endpoint={currentEndpoint}
              activeTabId={activeTabId}
              category={selectedCategory}
              style={{ width: codePanelWidth }}
              onLoadExample={(requestBody) => {
                setParams(buildParamsFromRequestBody(requestBody));
                setCurrentEndpoint(selectedModel?.endpoint || null);
                setTestResult(null);
              }}
            />
          </>
        ) : (
          <ContentPanel page={selectedPage} content={pageContent} />
        )}
      </div>
    </>
  );
};

export default Playground;
